import { useState, useEffect } from 'react';
import { FiX, FiEye, FiEyeOff, FiEdit2, FiCheck, FiUser } from 'react-icons/fi';
import { cpApi } from '../api/api';
import type { CounterpartyTeamMember } from '../types';

function getApiError(err: unknown): string {
  if (typeof err === 'string') return err;
  const e = err as { response?: { data?: string | { error?: string } }; message?: string };
  const data = e?.response?.data;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') return (data as { error?: string }).error || 'Ошибка';
  return e?.message || 'Ошибка';
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  member: CounterpartyTeamMember | null;
}

export default function EditTeamMemberWidget({ isOpen, onClose, onSuccess, member }: Props) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (member) {
      setLogin(member.login ?? '');
      setPassword('');
    }
  }, [member]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) {
      document.addEventListener('keydown', h);
      return () => document.removeEventListener('keydown', h);
    }
  }, [isOpen, onClose]);

  const handleUpdate = async (field: 'login' | 'password', value: string) => {
    if (!member) return;
    setLoading(true);
    setError(null);
    try {
      await cpApi.updateTeamMemberProfile(member.counterparty_id, { [field]: value || undefined });
      setEditingField(null);
      if (field === 'password') setPassword('');
      setSuccessMessage('✓ Обновлено');
      setTimeout(() => { setSuccessMessage(null); onSuccess(); }, 1500);
      if (field === 'login') setLogin(value);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !member) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 bg-gradient-to-r from-blue-600 to-indigo-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <FiUser className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{member.counterparty_name || `ID ${member.counterparty_id}`}</h2>
                <p className="text-blue-100 text-sm">Участник команды • ID: {member.id}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg text-white/80 hover:bg-white/20 transition-colors">
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-lg text-red-700 dark:text-red-200 text-sm">
              {error}
            </div>
          )}
          {successMessage && (
            <div className="p-3 bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900 rounded-lg text-green-700 dark:text-green-200 text-sm font-medium">
              {successMessage}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Логин</label>
            {editingField === 'login' ? (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 font-mono text-gray-900 dark:text-gray-100"
                  autoFocus
                />
                <button onClick={() => handleUpdate('login', login)} disabled={loading} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50">
                  <FiCheck className="w-5 h-5" />
                </button>
                <button onClick={() => setEditingField(null)} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-200">
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="flex justify-between items-center px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <span className="font-mono text-sm text-gray-900 dark:text-gray-100">{login || '—'}</span>
                <button onClick={() => setEditingField('login')} className="p-1.5 text-gray-400 hover:text-blue-500 rounded">
                  <FiEdit2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Пароль</label>
            {editingField === 'password' ? (
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Новый пароль"
                    className="w-full px-4 py-2.5 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 font-mono text-gray-900 dark:text-gray-100"
                    autoFocus
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                  </button>
                </div>
                <button onClick={() => password && handleUpdate('password', password)} disabled={loading || !password} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50">
                  <FiCheck className="w-5 h-5" />
                </button>
                <button onClick={() => { setEditingField(null); setPassword(''); }} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-200">
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="flex justify-between items-center px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <span className="font-mono text-sm text-gray-500 dark:text-gray-400">••••••••</span>
                <button onClick={() => setEditingField('password')} className="p-1.5 text-gray-400 hover:text-blue-500 rounded">
                  <FiEdit2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
